import { ErrorMessage } from 'views/components';
import { GenderPicker } from 'views/pickers';
import PropTypes from 'prop-types';
import { Row } from 'reactstrap';
import { StyledCol } from './styled';
import { patientActions } from 'core/Patient';
import { professionalActions } from 'core/Professional';
import { useTranslation } from 'react-i18next';
import {
    ButtonFooter,
    Container,
    DateSelect,
    ExpandButton,
    FormGroup,
    IconButton,
    Input,
    Modal,
    Text
} from 'mol-ui';
import React, { useEffect } from 'react';
import {
    objectToDate,
    validationSchema
} from 'shared/utils';
import {
    useCopyToClipboard,
    useDispatch,
    useErrors,
    useObjectState,
    useSelector
} from 'hooks';

const CLEAN_FORM = {
    email: '',
    first_name: '',
    gender: 'Female',
    identification: '',
    last_name: ''
};

const ExternalLoginModal = ({
    active,
    onToggle,
    profile
}) => {

    const {
        externalLogin: patientExternalLogin,
        loading
    } = useSelector('patient');
    const {
        externalLogin: professionalExternalLogin,
        externalLoginError
    } = useSelector('professional');
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [copyToClipboard, copiedToClipboard] = useCopyToClipboard();
    const [form, setForm, setFormValue] = useObjectState({ ...CLEAN_FORM });
    const { clearError, clearErrors, errors, setErrors } = useErrors();
    const externalUrl = patientExternalLogin || professionalExternalLogin;

    useEffect(() => {
        if (!active) return;
        !!professionalExternalLogin && dispatch(professionalActions.setProfessionalExternalLogin(''));
        !!externalLoginError && dispatch(professionalActions.setProfessionalExternalLoginError(''));
        !!patientExternalLogin && dispatch(patientActions.setPatientExternalLogin(''));
    }, [active]);
    
    const handleformChange = (id, value) => {
        clearError(id);
        setFormValue(id, value);
    };

    const handleSubmit = () => {
        if (!validate()) return;
        const data = {
            ...form,
            ...(!!form.birth_date && { birth_date: objectToDate(form.birth_date).format('YYYY-MM-DD') })
        };
        const createAction = {
            patient: patientActions.createPatientExternalLogin,
            professional: professionalActions.createProfessionalExternalLogin
        };
        dispatch(createAction[profile](data));
    };
    
    const onCancel = () => {
        onToggle();
        clearErrors();
        setForm({ ...CLEAN_FORM });
        !!professionalExternalLogin && dispatch(professionalActions.setProfessionalExternalLogin(''));
        !!externalLoginError && dispatch(professionalActions.setProfessionalExternalLoginError(''));
        !!patientExternalLogin && dispatch(patientActions.setPatientExternalLogin(''));
    };

    const validate = () => {
        const { errors, isValid } = validationSchema({
            email: [!!form.email, t('Requerido')],
            first_name: [!!form.first_name, t('Requerido')],
            identification: [!!form.identification, t('Requerido')],
            last_name: [!!form.last_name, t('Requerido')]
        });
        setErrors(errors);
        return isValid;
    };

    return (
        <Modal
            active={ active }
            padding="20px 10px"
            width="900px"
            onToggle={ onToggle }
        >
            <Text
                fontSize="20px"
                margin="0px 0px 30px 0px"
                textAlign="center"
            >
                { t('Generando login externo') }
            </Text>
            <Row>
                <StyledCol sm="6" xs="12">
                    <FormGroup label={ t('Apellido') }>
                        <Input
                            error={ errors.last_name }
                            margin="0px"
                            value={ form.last_name }
                            onChange={ value => handleformChange('last_name', value) }
                        />
                    </FormGroup>
                </StyledCol>
                <StyledCol sm="6" xs="12">
                    <FormGroup label={ t('Nombre') }>
                        <Input
                            error={ errors.first_name }
                            margin="0px"
                            value={ form.first_name }
                            onChange={ value => handleformChange('first_name', value) }
                        />
                    </FormGroup>
                </StyledCol>
                <StyledCol sm="6" xs="12">
                    <FormGroup label={ t('DNI') }>
                        <Input
                            error={ errors.identification }
                            margin="0px"
                            type="number"
                            value={ form.identification }
                            onChange={ value => handleformChange('identification', value) }
                        />
                    </FormGroup>
                </StyledCol>
                <StyledCol sm="6" xs="12">
                    <FormGroup label={ t('Fecha de Nac.') }>
                        <DateSelect
                            margin="0px"
                            value={ form.birth_date }
                            onChange={ value => handleformChange('birth_date', value) }
                        />
                    </FormGroup>
                </StyledCol>
                <StyledCol sm="6" xs="12">
                    <FormGroup label={ t('Email') }>
                        <Input
                            error={ errors.email }
                            margin="0px"
                            value={ form.email }
                            onChange={ value => handleformChange('email', value) }
                        />
                    </FormGroup>
                </StyledCol>
                <StyledCol sm="6" xs="12">
                    <FormGroup label={ t('Género') }>
                        <GenderPicker
                            margin="0px 0px 10px 0px"
                            value={ form.gender }
                            onChange={ value => handleformChange('gender', value) }
                        />
                    </FormGroup>
                </StyledCol>
            </Row>
            
            {
                !!externalLoginError &&
                <Row>
                    <StyledCol xs="12">
                        <ErrorMessage relative error={ externalLoginError } margin="0px" />
                    </StyledCol>
                </Row>
            }
            {
                !!externalUrl &&
                <Row>
                    <StyledCol xs="12">
                        <FormGroup label={ `${ t('URL') }:` }>
                            <Container alignItems="center" flexDirection="row">
                                <Text fontSize="14px" margin="0px 5px 0px 0px" wordBreak="break-word">
                                    { externalUrl }
                                </Text>
                                <IconButton
                                    icon={ copiedToClipboard ? 'done' : 'content_copy' }
                                    size={ 14 }
                                    onClick={ copiedToClipboard ? () => null : () => copyToClipboard(externalUrl) }
                                />
                            </Container>
                        </FormGroup>
                    </StyledCol>
                </Row>
            }
            <ButtonFooter margin="30px 0px 0px 0px">
                <ExpandButton
                    danger
                    caption={ t('Cancelar') }
                    size={ 120 }
                    onClick={ onCancel }
                />
                <ExpandButton
                    caption={ t('Aceptar') }
                    loading={ loading }
                    size={ 120 }
                    onClick={ handleSubmit }
                />
            </ButtonFooter>
        </Modal>
    );
};

ExternalLoginModal.propTypes = {
    active: PropTypes.bool,
    onToggle: PropTypes.func,
    profile: PropTypes.oneOf(['patient', 'professional'])
};

export default ExternalLoginModal;
